import { useQuery } from '@tanstack/react-query';
import type { ISiteLocations } from '@interfaces/IScheduler';
import { fetchSiteLocations } from '@apis/sites';
import { siteLocationsKey } from '@constants/queryKeys';

/**
 * Real building area catalog (floors → zones) for a site, replacing the v1 mock.
 * Backed by `GET /api/sites/:siteId/locations` (Trusstor-sourced). On loading,
 * error, or an unlinked site it returns an EMPTY catalog so the Scheduler's
 * Locations tab shows its "no areas" placeholder instead of erroring — the tab
 * already keys its rendering off `floors.length`.
 */
// One empty catalog for every caller, so memos keyed on `floors` hold while
// the catalog loads (or never will, on an unlinked site).
const NO_FLOORS: ISiteLocations['floors'] = [];

export function useSiteLocations(siteId: string): ISiteLocations {
  const { data } = useQuery({
    queryKey: siteLocationsKey(siteId),
    queryFn: ({ signal }) => fetchSiteLocations(siteId, signal),
    enabled: siteId !== '',
    staleTime: 30 * 60_000,
    // An unlinked site is a steady-state 409, not a transient failure — don't retry.
    retry: false,
  });
  return data ?? { siteId, floors: NO_FLOORS };
}
